import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chip, ChipProps, SpinningLoader } from '@frontend/design-system';
import { APPT_STATUS_INFO_MAP } from '../../constants';

export type StatusChipProps = Partial<ChipProps> & {
  status?: string;
  isIconOnly?: boolean;
  isLoading?: boolean;
  bordered?: boolean;
  disabled?: boolean;
};

export const StatusChip = forwardRef<HTMLDivElement, StatusChipProps>(
  ({ status, isIconOnly, isLoading, children, bordered = false, disabled = false, ...rest }, ref) => {
    const mappedProps = APPT_STATUS_INFO_MAP[status as EventStatus];

    if (!mappedProps) return null;

    const { color, label, chipVariant, icon } = mappedProps;

    if (isLoading) {
      return <SpinningLoader size='xxs' css={spinnerStyle(color)} />;
    }

    const iconElement = <Icon name={icon} css={{ color, fontSize: isIconOnly ? '1.5rem' : undefined }} />;

    if (isIconOnly) {
      return (
        <Chip.Tag ref={ref} {...rest} variant={chipVariant} css={chipStyle({ bordered, compact: true, disabled })}>
          {iconElement}
        </Chip.Tag>
      );
    }

    return (
      <Chip.Tag
        ref={ref}
        leftElement={iconElement}
        variant={chipVariant}
        {...rest}
        css={chipStyle({ bordered, disabled })}
      >
        {label}
        {children}
      </Chip.Tag>
    );
  }
);

StatusChip.displayName = 'StatusChip';

const chipStyle = ({
  bordered = false,
  compact = false,
  disabled = false,
}: {
  bordered?: boolean;
  compact?: boolean;
  disabled?: boolean;
}) =>
  css({
    border: bordered ? '1px solid' : 'none',
    padding: compact ? theme.spacing(0.25, 0.5) : theme.spacing(0.5, 1),
    cursor: disabled ? 'not-allowed' : 'pointer',
  });

const spinnerStyle = (color: string) =>
  css({
    borderRadius: theme.borderRadius.small,
    background: `color-mix(in srgb, ${color} 10%, ${theme.colors.white})`,
  });
